/**
 * Pozisyon Şablonu Yönetimi (admin)
 *
 * Tek pencerede:
 *  - Mevcut şablonlar listesi + personel kullanım sayısı
 *  - Inline rename (cascade → tüm personel pozisyonu güncellenir)
 *  - Delete (in-use şablonlar bloklanır, hata 409)
 *  - Merge (A → B: tüm personeli B'ye taşır, A silinir)
 *  - Legacy drift migration: personel'in şablonda olmayan pozisyonlarını
 *    fuzzy suggestion ile yeniden eşleyip toplu uygula
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy as CopyIcon, X, Edit2, Trash2, GitMerge, ArrowRight, AlertTriangle, CheckCircle2, Users, Palette, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getPozisyonColor, setCustomPozisyonColors } from "@/lib/pozisyonColors";
import { PositionRulesRow } from "./PositionRulesRow";
import { PositionCopyModal } from "./PositionCopyModal";
import { PositionImpactModal } from "./PositionImpactModal";

export default function PositionTemplateManager({ onClose, onChanged, yil, ay }) {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("templates"); // "templates" | "drift"
  const [templates, setTemplates] = useState([]);
  const [drift, setDrift] = useState({ groups: [], template_names: [] });
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [mergeFromId, setMergeFromId] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  // drift migration selections: { "<legacy pozisyon>": "<target template name>" }
  const [driftTargets, setDriftTargets] = useState({});
  const [busy, setBusy] = useState(false);
  const [copyFrom, setCopyFrom] = useState(null);
  const [impact, setImpact] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/position-templates"),
        api.get("/position-templates/drift"),
        api.get("/position-templates/colors").catch(() => ({ data: { colors: {} } })),
      ]);
      setTemplates(a.data.rows || []);
      setDrift(b.data || { groups: [], template_names: [] });
      // Hydrate global color cache so badges across app reflect current state
      setCustomPozisyonColors(c.data?.colors || {});
      // Pre-select first suggestion for each drift group when available.
      const next = {};
      (b.data.groups || []).forEach((g) => {
        if (g.suggestions?.[0]) next[g.pozisyon] = g.suggestions[0].isim;
      });
      setDriftTargets(next);
    } catch {
      toast.error("Şablonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const notifyParent = () => { onChanged?.(); };

  const saveColor = async (t, color) => {
    // color = "#RRGGBB" or null to clear
    setBusy(true);
    try {
      await api.patch(`/position-templates/${t.id}/color`, { color });
      toast.success(color ? "Renk kaydedildi" : "Özel renk kaldırıldı");
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renk kaydedilemedi");
    } finally { setBusy(false); }
  };

  const startEdit = (t) => { setEditId(t.id); setEditName(t.isim); setMergeFromId(null); };
  const cancelEdit = () => { setEditId(null); setEditName(""); };
  const saveYemek = (t, raw) => {
    const val = raw === "" ? null : Number(raw);
    if ((t.varsayilan_yemek ?? null) === val) return;
    setImpact({ title: `${t.isim} · günlük yemek ${val == null ? "boş" : `${val} ₺`}`, changes: { [t.id]: { varsayilan_yemek: val } },
      run: () => doSaveYemek(t, val), onCancel: load });
  };
  const doSaveYemek = async (t, val) => {
    try {
      await api.put(`/position-templates/${t.id}`, {
        isim: t.isim, varsayilan_sablon_id: t.varsayilan_sablon_id ?? null,
        varsayilan_maas: t.varsayilan_maas ?? null, varsayilan_maas_tipi: t.varsayilan_maas_tipi || "net",
        varsayilan_yemek: val, color: t.color ?? null,
      });
      toast.success(`${t.isim} · günlük yemek ${val == null ? "kaldırıldı" : `${val} ₺`}`);
      await load();
      notifyParent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const patchPos = (t, patch, msg) => setImpact({ title: `${t.isim} · ${msg}`, changes: { [t.id]: patch }, run: () => doPatch(t, patch, msg) });
  const doPatch = async (t, patch, msg) => {
    setBusy(true);
    try {
      await api.put(`/position-templates/${t.id}`, {
        isim: t.isim, varsayilan_sablon_id: t.varsayilan_sablon_id ?? null,
        varsayilan_maas: t.varsayilan_maas ?? null, varsayilan_maas_tipi: t.varsayilan_maas_tipi || "net",
        varsayilan_yemek: t.varsayilan_yemek ?? null, color: t.color ?? null, ...patch,
      });
      toast.success(`${t.isim} · ${msg}`);
      await load();
      notifyParent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); } finally { setBusy(false); }
  };
  const clearPersonal = async (t) => {
    if (!window.confirm(`"${t.isim}" pozisyonundaki ${t.override_count} personelin kişiye özel çalışma şablonu kaldırılsın mı? Pozisyonun çalışma günleri kullanılacak.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/position-templates/${t.id}/clear-personal-schedule`);
      toast.success(`${r.data.cleared} personel pozisyon günlerine bağlandı`);
      await load();
      notifyParent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Uygulanamadı"); } finally { setBusy(false); }
  };
  const saveEdit = async (t) => {
    if (!editName.trim() || editName.trim() === t.isim) { cancelEdit(); return; }
    setBusy(true);
    try {
      const r = await api.put(`/position-templates/${t.id}`, {
        isim: editName.trim(),
        varsayilan_sablon_id: t.varsayilan_sablon_id ?? null,
        varsayilan_maas: t.varsayilan_maas ?? null,
        varsayilan_maas_tipi: t.varsayilan_maas_tipi || "net",
        varsayilan_yemek: t.varsayilan_yemek ?? null,
        color: t.color ?? null,
      });
      toast.success(`Yeniden adlandırıldı. ${r.data.cascaded_personnel} personel güncellendi.`);
      cancelEdit();
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally { setBusy(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`"${t.isim}" şablonunu silmek istiyor musunuz?${t.usage_count > 0 ? ` (${t.usage_count} personel kullanıyor — bloklanacak)` : ""}`)) return;
    setBusy(true);
    try {
      await api.delete(`/position-templates/${t.id}`);
      toast.success("Silindi");
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally { setBusy(false); }
  };

  const startMerge = (t) => { setMergeFromId(t.id); setMergeTargetId(""); setEditId(null); };
  const cancelMerge = () => { setMergeFromId(null); setMergeTargetId(""); };
  const submitMerge = async (t) => {
    if (!mergeTargetId || mergeTargetId === t.id) { toast.error("Hedef şablon seçin"); return; }
    const target = templates.find((x) => x.id === mergeTargetId);
    if (!window.confirm(`"${t.isim}" → "${target?.isim}" birleştirilecek. "${t.isim}" silinecek ve ${t.usage_count || 0} personel "${target?.isim}"'ye taşınacak. Onaylıyor musunuz?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/position-templates/${t.id}/merge`, { target_id: mergeTargetId });
      toast.success(`${r.data.moved} personel taşındı. "${r.data.deleted}" silindi.`);
      cancelMerge();
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Birleştirilemedi");
    } finally { setBusy(false); }
  };

  const applyDriftOne = async (poz) => {
    const target = (driftTargets[poz] || "").trim();
    if (!target) { toast.error("Hedef şablon seçin"); return; }
    setBusy(true);
    try {
      const r = await api.post("/position-templates/migrate-drift", {
        mappings: [{ from: poz, to_isim: target }],
      });
      toast.success(`${r.data.total_updated} personel '${target}'ye taşındı`);
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Taşınamadı");
    } finally { setBusy(false); }
  };

  const applyDriftAll = async () => {
    const mappings = Object.entries(driftTargets)
      .filter(([_, v]) => v && v.trim())
      .map(([k, v]) => ({ from: k, to_isim: v.trim() }));
    if (mappings.length === 0) { toast.error("Hedef seçili bir satır yok"); return; }
    if (!window.confirm(`${mappings.length} drift grubu toplu olarak taşınacak. Devam?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/position-templates/migrate-drift", { mappings });
      toast.success(`${r.data.total_updated} personel toplu olarak güncellendi`);
      await load();
      notifyParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Taşınamadı");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !busy && onClose?.()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="position-template-manager"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="font-bold text-slate-900">Pozisyon Şablonu Yönetimi</div>
          <button onClick={() => onClose?.()} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={16}/></button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-slate-200 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setTab("templates")}
            data-testid="ptm-tab-templates"
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md ${tab==="templates" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            Şablonlar ({templates.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("drift")}
            data-testid="ptm-tab-drift"
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-md inline-flex items-center gap-1 ${tab==="drift" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            {drift.groups.length > 0 && <AlertTriangle size={11} />}
            Drift Tespiti ({drift.groups.length})
          </button>
        </div>

        <div className="p-5">
          {loading && <div className="text-sm text-slate-500">Yükleniyor...</div>}

          {!loading && tab === "templates" && (
            <div className="space-y-2">
              {templates.length === 0 && (
                <div className="text-sm italic text-slate-400">Henüz şablon yok.</div>
              )}
              {templates.map((t) => {
                const pal = getPozisyonColor(t.isim);
                const isCustom = !!t.color;
                return (
                <div key={t.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" data-testid={`ptm-row-${t.id}`}>
                  {/* View / edit row */}
                  <div className="flex items-center gap-2">
                    {/* Live color swatch — hex picker + reset */}
                    <div className="flex items-center gap-1 shrink-0">
                      <label
                        className="relative w-7 h-7 rounded-md border-2 cursor-pointer hover:scale-110 transition-transform grid place-items-center"
                        style={{ backgroundColor: pal.dotBg, borderColor: pal.chipBorder }}
                        title={isCustom ? `Özel renk: ${t.color}` : "Otomatik renk (hash) — tıklayıp özelleştir"}
                        data-testid={`ptm-color-swatch-${t.id}`}
                      >
                        <input
                          type="color"
                          value={t.color || pal.dotBg}
                          onChange={(e) => saveColor(t, e.target.value.toUpperCase())}
                          disabled={busy}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          data-testid={`ptm-color-input-${t.id}`}
                        />
                        <Palette size={11} className="pointer-events-none" style={{ color: pal.dotText }} />
                      </label>
                      {isCustom && (
                        <button
                          type="button"
                          onClick={() => saveColor(t, null)}
                          disabled={busy}
                          className="w-5 h-5 grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded"
                          title="Özel rengi kaldır (hash renge dön)"
                          data-testid={`ptm-color-reset-${t.id}`}
                        >
                          <RotateCcw size={10}/>
                        </button>
                      )}
                    </div>
                    {editId === t.id ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") cancelEdit(); }}
                        className="input flex-1"
                        data-testid={`ptm-rename-input-${t.id}`}
                      />
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 flex-1 text-xs font-bold px-2 py-1 rounded-md border"
                        style={{ backgroundColor: pal.chipBg, borderColor: pal.chipBorder, color: pal.chipText }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pal.dotBg }} />
                        {t.isim}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
                      <Users size={10}/> {t.usage_count ?? 0}
                    </span>
                    <label className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500" title="Bu pozisyondaki personelin varsayılan günlük yemek ücreti (kişiye özel tutar yoksa)">
                      Yemek/gün
                      <input key={`${t.id}-${t.varsayilan_yemek ?? ""}`} type="number" min="0" step="0.01" disabled={!isAdmin || t.yemek_yok}
                        defaultValue={t.varsayilan_yemek ?? ""} placeholder="—" data-testid={`ptm-yemek-${t.id}`}
                        onBlur={(e) => saveYemek(t, e.target.value)}
                        className="w-20 px-1.5 py-0.5 border border-slate-300 rounded text-xs font-mono-tab disabled:bg-slate-100" />
                    </label>
                    {editId === t.id ? (
                      <>
                        <button onClick={() => saveEdit(t)} disabled={busy} className="text-[11px] font-semibold px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded">Kaydet</button>
                        <button onClick={cancelEdit} disabled={busy} className="text-[11px] font-semibold px-2 py-1 text-slate-600 hover:bg-slate-100 rounded">İptal</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(t)} className="w-7 h-7 grid place-items-center hover:bg-indigo-50 text-indigo-600 rounded" title="Yeniden adlandır (cascade)" data-testid={`ptm-rename-${t.id}`}>
                          <Edit2 size={13}/>
                        </button>
                        <button onClick={() => setCopyFrom(t)} className="w-7 h-7 grid place-items-center hover:bg-teal-50 text-teal-700 rounded" title="Yemek, yol ve çalışma günü ayarlarını başka pozisyonlara kopyala" data-testid={`ptm-copy-${t.id}`}>
                          <CopyIcon size={13}/>
                        </button>
                        <button onClick={() => startMerge(t)} className="w-7 h-7 grid place-items-center hover:bg-amber-50 text-amber-700 rounded" title="Başka şablonla birleştir" data-testid={`ptm-merge-${t.id}`}>
                          <GitMerge size={13}/>
                        </button>
                        <button onClick={() => handleDelete(t)} className="w-7 h-7 grid place-items-center hover:bg-rose-50 text-rose-600 rounded" title={t.usage_count > 0 ? `${t.usage_count} personel kullanıyor — önce birleştirin` : "Sil"} data-testid={`ptm-delete-${t.id}`}>
                          <Trash2 size={13}/>
                        </button>
                      </>
                    )}
                  </div>

                  <PositionRulesRow t={t} isAdmin={isAdmin} busy={busy} onSave={patchPos} onClearPersonal={clearPersonal} />

                  {/* Merge expand */}
                  {mergeFromId === t.id && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2" data-testid={`ptm-merge-panel-${t.id}`}>
                      <span className="text-xs font-semibold text-amber-900 shrink-0">{t.isim}</span>
                      <ArrowRight size={13} className="text-amber-700 shrink-0"/>
                      <select
                        value={mergeTargetId}
                        onChange={(e) => setMergeTargetId(e.target.value)}
                        className="input flex-1 text-xs"
                        data-testid={`ptm-merge-target-${t.id}`}
                      >
                        <option value="">— Hedef şablon seçin —</option>
                        {templates.filter((x) => x.id !== t.id).map((x) => (
                          <option key={x.id} value={x.id}>{x.isim}</option>
                        ))}
                      </select>
                      <button onClick={() => submitMerge(t)} disabled={busy || !mergeTargetId} className="text-[11px] font-semibold px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50" data-testid={`ptm-merge-submit-${t.id}`}>Birleştir</button>
                      <button onClick={cancelMerge} disabled={busy} className="text-[11px] font-semibold px-2 py-1 text-slate-600 hover:bg-slate-100 rounded">İptal</button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {!loading && tab === "drift" && (
            <div className="space-y-3">
              {drift.groups.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center text-sm text-emerald-800 inline-flex items-center justify-center gap-2 w-full" data-testid="ptm-drift-clean">
                  <CheckCircle2 size={16}/> Tüm personel pozisyonları mevcut şablonlarla eşleşiyor. Drift yok.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <div className="text-xs text-amber-900">
                      <strong>{drift.groups.length} legacy pozisyon</strong> hiçbir şablona eşleşmiyor. Önerilen eşleşmeleri gözden geçirin ve toplu uygulayın.
                    </div>
                    <button
                      type="button"
                      onClick={applyDriftAll}
                      disabled={busy}
                      className="shrink-0 text-xs font-semibold px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-50"
                      data-testid="ptm-drift-apply-all"
                    >
                      Tümünü Uygula
                    </button>
                  </div>
                  {drift.groups.map((g) => (
                    <div key={g.pozisyon} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`ptm-drift-row-${g.pozisyon}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={14} className="text-amber-600"/>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-900 text-sm">{g.pozisyon}</div>
                          <div className="text-[11px] text-slate-500">
                            {g.personnel_count} personel
                            {g.personnel.slice(0, 3).map((p, i) => (
                              <span key={p.id}> · {p.ad_soyad}</span>
                            ))}
                            {g.personnel.length > 3 && <span> · +{g.personnel.length - 3} daha</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <ArrowRight size={13} className="text-slate-400 shrink-0"/>
                        <select
                          value={driftTargets[g.pozisyon] || ""}
                          onChange={(e) => setDriftTargets((p) => ({ ...p, [g.pozisyon]: e.target.value }))}
                          className="input flex-1 text-xs"
                          data-testid={`ptm-drift-target-${g.pozisyon}`}
                        >
                          <option value="">— Hedef şablon —</option>
                          {drift.template_names.map((n) => (
                            <option key={n} value={n}>
                              {n}
                              {g.suggestions.find((s) => s.isim === n) ? ` · önerilen (${Math.round(g.suggestions.find((s) => s.isim === n).score * 100)}%)` : ""}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => applyDriftOne(g.pozisyon)}
                          disabled={busy || !driftTargets[g.pozisyon]}
                          className="text-[11px] font-semibold px-2 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-50"
                          data-testid={`ptm-drift-apply-${g.pozisyon}`}
                        >
                          Uygula
                        </button>
                      </div>
                      {g.suggestions.length > 0 && (
                        <div className="mt-1 text-[10px] text-slate-500">
                          Öneri: {g.suggestions.map((s) => `${s.isim} (${Math.round(s.score * 100)}%)`).join(" · ")}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {copyFrom && <PositionCopyModal source={copyFrom} templates={templates} isAdmin={isAdmin} onPreview={setImpact}
        onClose={() => setCopyFrom(null)} onDone={() => { setCopyFrom(null); load(); notifyParent(); }} />}
      {impact && <PositionImpactModal yil={yil} ay={ay} title={impact.title} changes={impact.changes}
        onClose={() => { impact.onCancel?.(); setImpact(null); }} onConfirm={async () => { await impact.run(); setImpact(null); }} />}
    </div>
  );
}
